import styled from '@emotion/styled';
import React, { useCallback, useEffect } from 'react';
import { ActionType } from '@diritto/common/app/inapp-event.constant';
import { CONTENT_HELP_URL } from '@diritto/common/constants/url';
import {
  AgeRestrictionList,
  AgeRestrictionNonAdultList,
  AgeRestrictionString,
  CategoryTypeString,
  ContentSerializeStatus,
  ContentSerializeStatusString,
  ContentStatus,
  ContentStatusString,
  UsageTypeList,
  UsageTypeString,
} from '@diritto/common/content/constants';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import {
  ClickAction,
  ClickTargetType,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { convertToDotString } from '@diritto/common/utils/date';
import { ContentKeywordInput } from 'app-components/contents/editor/ContentKeywordInput';
import { ContentSelectType } from 'app-components/contents/editor/ContentSelectType';
import { ContentTextArea } from 'app-components/contents/editor/ContentTextArea';
import { EditContentCover } from 'app-components/contents/editor/EditContentCover';
import { ButtonSize, ButtonType } from 'constants/button';
import { Font } from 'constants/contentCover';
import { ContentBodyPadding, isPC } from 'constants/layout';
import { ModalType } from 'constants/modal';
import { CategoryTypeDisplayOnEditorList } from 'constants/navigation';
import { useAuthState } from 'contexts/AuthProvider';
import { useInAppState } from 'contexts/InAppProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { CalendarEditIcon } from 'icons/CalendarEditIcon';
import { HelpIcon } from 'icons/HelpIcon';
import { Button } from 'ui-components/common/Button';
import { CheckBox } from 'ui-components/common/CheckBox';
import { ExternalLink } from 'ui-components/common/ExternalLink';
import { ExternalLinkButton } from 'ui-components/common/ExternalLinkButton';
import { Input } from 'ui-components/common/Input';
import { Loading } from 'ui-components/common/Loading';
import { PrefixInput } from 'ui-components/common/PrefixInput';
import { RadioButton } from 'ui-components/common/RadioButton';
import { SwitchButton, SwitchButtonSize } from 'ui-components/common/SwitchButton';
import { Modal } from 'ui-components/modals/Modal';
import { SelectDateModal } from 'ui-components/modals/SelectDateModal';
import { useLoadFont } from 'ui-hooks/useLoadFont';
import { EditContentData } from 'utils/editContent';
import { ModalHandler } from 'utils/ModalHandler';
import { FormikType } from 'utils/types/formik';

interface EditContentProps {
  formik: FormikType<EditContentData>;
  isLoadingSubmit?: boolean;
  className?: string;
  isChanged?: boolean;
  contentId?: number;
}

const EditContentSerializeStatusList = [
  ContentSerializeStatus.Serialized,
  ContentSerializeStatus.Completed,
  ContentSerializeStatus.Published,
];

const EditContentInput = React.memo(Input);
const EditContentSelectType = React.memo(ContentSelectType);
const EditContentTextArea = React.memo(ContentTextArea);

const trackerExtraInfo = {
  trackerPosition: TrackerPosition.EditContent,
};

const sanitizeInput = (value: string) => {
  const forbiddenPattern = /[\u0300-\u036F\u1DC0-\u1DFF\uFE00-\uFE0F\u200B-\u200F]/g;
  return value.replace(forbiddenPattern, '');
};

export const EditContent = ({ className, isLoadingSubmit, isChanged, formik, contentId }: EditContentProps) => {
  const { user, profile } = useAuthState();
  const { tracker } = useTrackingState();

  const handleSetValue = useCallback(
    (field: string, value: any) => {
      formik.setFieldValue(field, value);
      formik.setFieldTouched(field, true);
    },
    [formik],
  );
  const handleChange = useCallback(
    (event: any) => {
      formik.handleChange(event);
      formik.handleBlur(event);
    },
    [formik],
  );

  const handleChangeTitle = useCallback(
    (event: any) => {
      const rawValue = event.target.value;
      const sanitized = sanitizeInput(rawValue);
      handleSetValue('title', sanitized);
    },
    [handleSetValue],
  );

  const handleChangeDescription = useCallback(
    (event: any) => {
      const rawValue = event.target.value;
      const sanitized = sanitizeInput(rawValue);
      handleSetValue('description', sanitized);
    },
    [handleSetValue],
  );

  const { addActionEventListener } = useInAppState();
  useEffect(
    () =>
      addActionEventListener(params => {
        switch (params.type) {
          case ActionType.Save:
          case ActionType.Register:
            formik.handleSubmit();
            break;
          default:
            break;
        }
      }),
    [addActionEventListener, formik],
  );

  const handleChangeReservedHideContentsAt = useCallback(() => {
    ModalHandler.show(ModalType.SelectDate, {
      title: '작품 내용 숨기기 예약',
      description: '예약될 날짜를 선택해 주세요.',
      confirmText: '예약',
      defaultValue: formik.values.reservedHideContentsAt,
      onConfirm: (reservedAt: Date) => {
        handleSetValue('isHiddenContents', false);
        handleSetValue('reservedHideContentsAt', reservedAt);
      },
    });
  }, [formik.values.reservedHideContentsAt, handleSetValue]);

  const handleHideContentsAt = useCallback(() => {
    tracker.track(TrackingEventType.ClickContentEditToChange, {
      contentId,
      target: 'setReservedHideContentsAt',
      trackerExtraInfo,
    });

    if (!formik.values.isHiddenContents && !formik.values.reservedHideContentsAt) {
      handleChangeReservedHideContentsAt();
    } else {
      handleSetValue('isHiddenContents', false);
      handleSetValue('reservedHideContentsAt', null);
    }
  }, [
    tracker,
    contentId,
    formik.values.isHiddenContents,
    formik.values.reservedHideContentsAt,
    handleChangeReservedHideContentsAt,
    handleSetValue,
  ]);

  const handleClickSave = useCallback(() => {
    tracker.track(TrackingEventType.Click, {
      clickType: ClickTargetType.Button,
      action: ClickAction.SaveContent,
      trackerExtraInfo,
    });
    formik.handleSubmit();
  }, [formik, tracker]);

  useLoadFont(Object.values(Font));

  return (
    <>
      <StyledWrapper className={className}>
        <StyledSubject>작품 제목</StyledSubject>
        <StyledEditContentInput id="title" value={formik.values.title} onChange={handleChangeTitle} />
        <StyledSubject>카테고리</StyledSubject>
        <EditContentSelectType
          id="category"
          list={CategoryTypeDisplayOnEditorList}
          stringMap={CategoryTypeString}
          defaultValue={formik.values.category}
          onChange={handleChange}
        />
        <StyledSubject>이용가</StyledSubject>
        <EditContentSelectType
          id="ageRestriction"
          list={user?.isAdult ? AgeRestrictionList : AgeRestrictionNonAdultList}
          stringMap={AgeRestrictionString}
          defaultValue={formik.values.ageRestriction}
          onChange={handleChange}
        />
        <StyledSubject>
          이미지 멋지게 공유하기 기능
          <StyledExternalLink href={CONTENT_HELP_URL.FANCY_SHARE_URL}>
            <HelpIcon color={Color.Gray500} className="size-[16px]" />
          </StyledExternalLink>
        </StyledSubject>
        <EditContentSelectType
          id="isFancyShareEnabled"
          list={UsageTypeList}
          stringMap={UsageTypeString}
          defaultValue={formik.values.isFancyShareEnabled}
          onChange={handleChange}
        />
        {!!contentId && (
          <>
            <StyledSubject>
              연재처
              <StyledExternalLink href={CONTENT_HELP_URL.DIRITTO_ONLY}>
                <HelpIcon color={Color.Gray500} className="size-[16px]" />
              </StyledExternalLink>
            </StyledSubject>
            <ExternalLinkButton
              label="디리토에서만 연재하는 것 알리기"
              href={`https://14nid3x2nun.typeform.com/to/CHJKqfux#profile_id=${profile?.id}&content_id=${contentId}&title=${formik.values.title}`}
              buttonType={ButtonType.Secondary}
              fullWidth
            />
          </>
        )}
        <StyledSubject>작품 설명</StyledSubject>
        <EditContentTextArea
          id="description"
          rows={7}
          value={formik.values.description}
          onChange={handleChangeDescription}
        />
        <StyledSubject>키워드</StyledSubject>
        <ContentKeywordInput
          defaultValue={formik.values.keywords}
          onChange={keywords => handleSetValue('keywords', keywords)}
        />
        <StyledSubject>표지</StyledSubject>
        <EditContentCover
          imageFile={formik.values.cover}
          onChangeCoverImage={cover => handleSetValue('cover', cover)}
        />
        <StyledSubject>작품 상태</StyledSubject>
        <StyledStatusContainer>
          <StyledStatusWrapper>
            <RadioButton
              name={ContentStatusString[ContentStatus.Public]}
              id="status"
              value={ContentStatus.Public}
              selectedValue={formik.values.status}
              onChange={() => handleSetValue('status', ContentStatus.Public)}
            />
            {formik.values.status === ContentStatus.Public && (
              <>
                <StyledPublicStatusWrapper>
                  <StyledPublicStatus
                    id="serializeStatus"
                    list={EditContentSerializeStatusList}
                    stringMap={ContentSerializeStatusString}
                    defaultValue={formik.values.serializeStatus}
                    onChange={handleChange}
                  />
                  {formik.values.serializeStatus === ContentSerializeStatus.Published && (
                    <StyledPublicStatusWrapper>
                      <PrefixInput
                        prefix="https://ridibooks.com/books/"
                        id="publishedLink"
                        value={formik.values.publishedLink.replace('https://ridibooks.com/books/', '')}
                        onChange={e => handleSetValue('publishedLink', `https://ridibooks.com/books/${e.target.value}`)}
                      />
                      <StyledContentPublishedLinkMessage>
                        숫자만 입력해주세요. / 작품 상세에 버튼으로 노출돼요.
                      </StyledContentPublishedLinkMessage>
                    </StyledPublicStatusWrapper>
                  )}
                </StyledPublicStatusWrapper>
                <StyledDivider />
                <StyledHiddenContentsWrapper>
                  <SwitchButton
                    id="isProposalWanted"
                    checked={!!formik.values.isProposalWanted}
                    onChange={() => handleSetValue('isProposalWanted', !formik.values.isProposalWanted)}
                    size={SwitchButtonSize.small}
                  />
                  <StyledHiddenContentsInfoWrapper>
                    <StyledHiddenContentsTextWrapper>
                      <p>출판사 계약 의지 밝히기</p>
                      <StyledExternalLink href={CONTENT_HELP_URL.PROPOSAL_CONTENTS}>
                        <HelpIcon color={Color.Gray500} className="size-[16px]" />
                      </StyledExternalLink>
                    </StyledHiddenContentsTextWrapper>
                  </StyledHiddenContentsInfoWrapper>
                </StyledHiddenContentsWrapper>
                <StyledDivider />
                <StyledHiddenContentsWrapper>
                  <SwitchButton
                    id="isHiddenContents"
                    checked={formik.values.isHiddenContents || !!formik.values.reservedHideContentsAt}
                    onChange={handleHideContentsAt}
                    size={SwitchButtonSize.small}
                  />
                  <StyledHiddenContentsInfoWrapper>
                    <StyledHiddenContentsTextWrapper>
                      {formik.values.isHiddenContents ? <p>작품 내용 숨기기</p> : <p>작품 내용 숨기기 예약</p>}
                      <StyledExternalLink href={CONTENT_HELP_URL.HIDDEN_CONTENTS}>
                        <HelpIcon color={Color.Gray500} className="size-[16px]" />
                      </StyledExternalLink>
                    </StyledHiddenContentsTextWrapper>
                    {!formik.values.isHiddenContents && formik.values.reservedHideContentsAt && (
                      <StyledHiddenContentsReserveWrapper>
                        <p>{convertToDotString(formik.values.reservedHideContentsAt)}</p>
                        <StyledHiddenContentsChangeReserveButton
                          onClick={() => {
                            tracker.track(TrackingEventType.ClickContentEditToChange, {
                              contentId,
                              target: 'changeReservedHideContentsAt',
                              trackerExtraInfo,
                            });
                            handleChangeReservedHideContentsAt();
                          }}>
                          <CalendarEditIcon size={16} color={Color.Red500} />
                          예약변경
                        </StyledHiddenContentsChangeReserveButton>
                      </StyledHiddenContentsReserveWrapper>
                    )}
                  </StyledHiddenContentsInfoWrapper>
                </StyledHiddenContentsWrapper>
              </>
            )}
          </StyledStatusWrapper>
          <StyledStatusWrapper>
            <RadioButton
              name={ContentStatusString[ContentStatus.Private]}
              id="status"
              value={ContentStatus.Private}
              selectedValue={formik.values.status}
              onChange={() => handleSetValue('status', ContentStatus.Private)}
            />
          </StyledStatusWrapper>
        </StyledStatusContainer>

        <StyledSubject>작품 관련 동의</StyledSubject>
        <CheckBox
          id="isMarketingAgreed"
          checked={formik.values.isMarketingAgreed}
          onChange={() => handleSetValue('isMarketingAgreed', !formik.values.isMarketingAgreed)}>
          <p>작품 마케팅 활용 동의</p>
          <StyledExternalLink href={CONTENT_HELP_URL.MARKETING_AGREED}>
            <HelpIcon color={Color.Gray500} className="size-[16px]" />
          </StyledExternalLink>
        </CheckBox>
        <StyledButtonWrapper>
          {isLoadingSubmit ? (
            <Button fullWidth label={<Loading />} size={ButtonSize.Medium} buttonTypography={Typography.XXSmall} />
          ) : (
            <Button
              fullWidth
              label={contentId ? '저장' : '작품 등록'}
              size={ButtonSize.Medium}
              buttonTypography={Typography.XXSmall}
              type="submit"
              disabled={!isChanged}
              onClick={handleClickSave}
            />
          )}
        </StyledButtonWrapper>
      </StyledWrapper>
      <Modal useBody>
        <SelectDateModal />
      </Modal>
    </>
  );
};

const StyledWrapper = styled.div`
  padding: ${ContentBodyPadding}px;

  ${Typography.XSmall};
  font-weight: ${FontWeight.Regular};
`;

const StyledEditContentInput = styled(EditContentInput)`
  border: 1px solid ${Color.Gray300};
`;

const StyledSubject = styled.p`
  display: flex;
  align-items: center;
  margin: 32px 0 8px 0;
  color: ${Color.Gray600};
  font-weight: ${FontWeight.Medium};
  gap: 4px;
  ${Typography.XXSmall};

  &:first-of-type {
    margin-top: 0;
  }
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 32px;
  gap: 16px;
`;

const StyledDivider = styled.div`
  width: 100%;
  height: 1px;
  background: ${Color.Gray300};
  margin: 16px 0;
`;

const StyledHiddenContentsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const StyledHiddenContentsInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledHiddenContentsTextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const StyledHiddenContentsReserveWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  color: ${Color.Red500};
  ${Typography.XXSmall};
  font-weight: ${FontWeight.Regular};
`;

const StyledHiddenContentsChangeReserveButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const StyledContentPublishedLinkMessage = styled.div`
  margin-top: 8px;
  color: ${Color.Gray500};
  font-weight: ${FontWeight.Medium};
  ${Typography.XXXSmall};
`;

const StyledStatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledStatusWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid ${Color.Gray300};

  ${Typography.XSmall};
  font-weight: ${FontWeight.Regular};
`;

const StyledPublicStatusWrapper = styled.div`
  margin-top: 8px;
  margin-left: 24px;
`;

const StyledPublicStatus = styled(EditContentSelectType)`
  display: flex;
  flex-direction: column;
  align-items: baseline;
  width: 100%;
  gap: 8px;

  @media (${isPC}) {
    flex-direction: column;
    align-items: baseline;
    gap: 8px;
  }
`;

const StyledExternalLink = styled(ExternalLink)`
  display: flex;
`;
